import { Box, IconButton, Modal, Typography, useTheme, Tooltip, Menu, MenuItem } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import eyeIcon from 'packages/novous_web/src/assets/icons/Eye.svg';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from 'packages/novous_web/src/assets/icons/Expand.svg';
import EditIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import RestartAltIcon from 'packages/novous_web/src/assets/icons/Reset.svg';
import CollapseIcon from 'packages/novous_web/src/assets/icons/CollapseIcon.svg';
import NumericalAnalysisTable from '../SafetyAnalytics/NumericalAnalysisTable';
import EditDataModal from './EditDataModal';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import CommonAnalysisTable from './CommonAnalysisTable';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

interface VehicleData {
  record_date: string;
  vehicle_id: string;
  warning_ratio: number;
}

interface CommonFloatingGraphProps {
  title?: string;
  data?: VehicleData[];
  chartId?: number;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
  infoIcon?: React.ReactNode;
  alertColorMapping?: { [key: string]: string };
  yAxisLabel?: string;
  tableColumns?: { key: string; label: string; align?: 'left' | 'center' | 'right' }[];
  tableData?: any[];
  tableTitle?: string;
  tableHeaderText?: string;
  paginationInfo?: {
    currentPage?: number;
    pageSize?: number;
    totalRecords?: number;
    hasMorePages?: boolean;
    nextCursor?: any;
    previousCursor?: any;
  };
  onTableIconClick?: () => void;
  onPaginationChange?: (cursor?: any, direction?: 'next' | 'previous') => void;
  onTablePaginationChange?: (newPage: number) => void;
  legendAlign?: 'flex-start' | 'center' | 'flex-end';
  paginatedData?: any;
  fleetAverage?: { fullDate: string; avg: number }[];
}

const CommonFloatingGraph: React.FC<CommonFloatingGraphProps> = ({
  title = 'Warning Per 100 Km',
  data = [],
  chartId,
  onCardDataUpdate,
  infoIcon,
  alertColorMapping,
  yAxisLabel,
  tableColumns = [],
  tableData = [],
  tableTitle,
  tableHeaderText,
  paginationInfo,
  onTableIconClick,
  onPaginationChange,
  onTablePaginationChange,
  legendAlign = 'flex-start',
  paginatedData,
  fleetAverage = [],
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [allVehicles, setAllVehicles] = useState<string[]>([]);
  const [top5Vehicles, setTop5Vehicles] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', title);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', title);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    if (onCardDataUpdate) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', title);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    if (onCardDataUpdate) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };

  const defaultColors = [
    '#5547DD',
    '#27AE60',
    '#C83333',
    '#F2994A',
    '#2F80ED',
    '#9B51E0',
    '#F44336',
    '#2196F3',
    '#00BCD4',
  ];

  // Extract unique vehicle IDs from data and get top 5
  useEffect(() => {
    if (data && data.length > 0) {
      // Get unique vehicles
      const uniqueVehicles = Array.from(new Set(data.map((item) => item.vehicle_id)));
      setAllVehicles(uniqueVehicles);

      // Get top 5 vehicles based on highest warning_ratio
      const vehicleStats: { [key: string]: number } = {};

      data.forEach((item) => {
        const vehicleId = item.vehicle_id;
        const warningRatio = item.warning_ratio || 0;

        if (!vehicleStats[vehicleId] || warningRatio > vehicleStats[vehicleId]) {
          vehicleStats[vehicleId] = warningRatio;
        }
      });

      // Sort vehicles by warning_ratio (highest first) and take first 5
      const sortedVehicles = Object.entries(vehicleStats)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([vehicleId]) => vehicleId);

      setTop5Vehicles(sortedVehicles);
    } else {
      setAllVehicles([]);
      setTop5Vehicles([]);
    }
  }, [data]);

  // Transform data for the chart - only for paginated vehicles
  useEffect(() => {
    if (data.length === 0 || allVehicles.length === 0) {
      setChartData([]);
      return;
    }

    // Group data by date
    const dataByDate: { [key: string]: any } = {};

    data.forEach((item: VehicleData) => {
      // Only include data for paginated vehicles
      if (!paginatedVehicles.includes(item.vehicle_id)) {
        return;
      }

      const date = item.record_date;

      if (!dataByDate[date]) {
        // Format date for display (e.g., "02 Dec")
        const dateObj = new Date(date);
        const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')} ${dateObj.toLocaleString(
          'default',
          { month: 'short' },
        )}`;

        dataByDate[date] = {
          date: formattedDate,
          fullDate: date,
        };
      }

      // Add vehicle's warning ratio for this date
      dataByDate[date][item.vehicle_id] = parseFloat(item.warning_ratio.toFixed(2));
    });

    // Convert to array and sort by date
    const chartDataArray = Object.values(dataByDate).sort(
      (a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime(),
    );

    // Attach fleet average values (if provided) to each chart data point
    if (fleetAverage && fleetAverage.length > 0) {
      const fleetMap = new Map(fleetAverage.map((f) => [f.fullDate, f.avg]));
      chartDataArray.forEach((item: any) => {
        item.fleetAvg = fleetMap.get(item.fullDate) ?? null;
      });
    }

    setChartData(chartDataArray);
  }, [data, allVehicles, page, fleetAverage]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      const currentData = payload[0]?.payload;
      const fullDate = currentData?.fullDate || label;

      return (
        <Box className="bg-white p-3 border rounded shadow-lg">
          <Typography
            variant="body2"
            fontSize={12}
            sx={(theme) => ({
              color: theme.palette.text.primary,
              fontWeight: 600,
            })}
          >
            {fullDate}
          </Typography>

          {payload.map((entry: any, index: number) => (
            <Box key={index} className="flex items-center mb-1">
              <Box className="w-3 h-3 mr-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <Typography
                variant="body2"
                fontSize={12}
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                })}
              >
                {entry.name}: <strong>{entry.value?.toFixed(2)}</strong>
              </Typography>
            </Box>
          ))}

          <Box className="flex items-center mt-2 pt-2 border-t">
            <Box
              className="w-3 h-3 mr-2 rounded-full"
              style={{ backgroundColor: theme.palette.text.primary }}
            />
            <Typography
              variant="body2"
              fontSize={12}
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 600,
              })}
            >
              Total: <strong>{total.toFixed(2)}</strong>
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Cursor history for pagination (like BarChartCommon)
  const [cursorHistory, setCursorHistory] = useState<any[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState<number>(-1);
  const [pageHistory, setPageHistory] = useState<any[]>([]);
  const [sentNextCursors, setSentNextCursors] = useState<any[]>([]);
  const [currentPageState, setCurrentPageState] = useState<number | null>(null);

  // Check if we have next/previous cursors
  const hasNextCursor = Boolean(paginatedData?.next_cursor ?? paginationInfo?.nextCursor);
  const hasPreviousCursor =
    currentCursorIndex >= 0 || (paginatedData?.previous_cursor ?? paginationInfo?.previousCursor);

  // Compute current page
  const currentPage = useMemo(() => {
    if (currentPageState !== null) {
      return currentPageState;
    }

    if (paginationInfo?.currentPage !== undefined && paginationInfo?.currentPage !== null) {
      return paginationInfo.currentPage;
    }

    if (currentCursorIndex >= 0) {
      return currentCursorIndex + 2;
    }

    return 1;
  }, [currentPageState, paginationInfo?.currentPage, currentCursorIndex]);

  // Effect to store server-provided next cursor for the subsequent page
  useEffect(() => {
    if (paginatedData?.next_cursor) {
      const serverCurrentPage = paginationInfo?.currentPage ?? 1;
      const nextPage = serverCurrentPage + 1;

      const exists = pageHistory.some(
        (p) =>
          p.pageNum === nextPage && JSON.stringify(p.cursor) === JSON.stringify(paginatedData.next_cursor),
      );
      if (!exists) {
        setPageHistory((prev) => {
          const filtered = prev.filter((p) => p.pageNum !== nextPage);
          return [...filtered, { cursor: paginatedData.next_cursor, pageNum: nextPage }];
        });
      }
    }
  }, [paginatedData?.next_cursor, paginationInfo?.currentPage, pageHistory]);

  // Clear the local page override when the server's currentPage matches it
  useEffect(() => {
    if (
      currentPageState !== null &&
      paginationInfo?.currentPage !== undefined &&
      paginationInfo.currentPage === currentPageState
    ) {
      setCurrentPageState(null);
    }
  }, [paginationInfo?.currentPage, currentPageState]);

  // Handle pagination: use cursor-based if available, otherwise use page-based
  const handlePrevPage = () => {
    // For cursor-based pagination
    const prevCursorAvail = paginatedData?.previous_cursor ?? paginationInfo?.previousCursor;

    if (onPaginationChange) {
      if (cursorHistory.length > 0 && currentCursorIndex > -1) {
        // We have previous pages in history
        const newIndex = Math.max(currentCursorIndex - 1, -1);
        setCurrentCursorIndex(newIndex);

        if (newIndex === -1) {
          // Going back to page 1 (no cursor needed)
          setCurrentPageState(1);
          onPaginationChange(null, 'previous');
        } else {
          const prevCursor = cursorHistory[newIndex];
          setCurrentPageState(newIndex + 1 + 1); // newIndex 0 => page 2
          onPaginationChange(prevCursor, 'previous');
        }
      }
    } else if (!onPaginationChange) {
      // Fallback to simple page-based pagination for vehicles
      setPage((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleNextPage = () => {
    // For cursor-based pagination
    const nextCursorAvail = paginatedData?.next_cursor ?? paginationInfo?.nextCursor;

    if (onPaginationChange && nextCursorAvail) {
      setSentNextCursors((prev) => [...prev, nextCursorAvail]);

      const newIndex = currentCursorIndex + 1;
      if (cursorHistory.length <= newIndex) {
        const newHistory = [...cursorHistory, nextCursorAvail];
        setCursorHistory(newHistory);
        setCurrentCursorIndex(newIndex);

        const newPageHistory = [
          ...pageHistory,
          {
            cursor: nextCursorAvail,
            pageNum: newIndex + 2,
          },
        ];
        setPageHistory(newPageHistory);
      } else {
        setCurrentCursorIndex(newIndex);
      }

      setCurrentPageState((prev) => (prev !== null ? prev + 1 : (paginationInfo?.currentPage ?? 1) + 1));
      onPaginationChange(nextCursorAvail, 'next');
    } else if (!hasNextCursor || !onPaginationChange) {
      // Fallback to simple page-based pagination for vehicles
      const totalPages = Math.ceil(allVehicles.length / ITEMS_PER_PAGE);
      setPage((prev) => Math.min(prev + 1, totalPages - 1));
    }
  };

  // Pagination logic - use allVehicles for pagination (when not using server-side)
  const totalPages = Math.ceil(allVehicles.length / ITEMS_PER_PAGE);
  const handlePrev = () => {
    handlePrevPage();
  };
  const handleNext = () => {
    handleNextPage();
  };

  // Get paginated vehicle IDs from all vehicles
  const paginatedVehicles = allVehicles.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE);

  const renderCustomizedLegend = () => {
    // Determine if we should show previous button
    const showPreviousButton = onPaginationChange
      ? hasPreviousCursor || currentCursorIndex > -1 // For cursor-based pagination
      : page > 0; // For client-side vehicle pagination

    // Determine if we should show next button
    const showNextButton = onPaginationChange
      ? hasNextCursor // For cursor-based pagination
      : page < totalPages - 1; // For client-side vehicle pagination

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          gap: 1,
        }}
      >
        {showPreviousButton && (
          <IconButton
            size="small"
            onClick={handlePrev}
            sx={{
              p: 0.5,
              color: theme.palette.text.primary,
              flexShrink: 0,
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ width: 16, height: 16, opacity: 1 }} />
          </IconButton>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2, md: 3 },
            flexGrow: 1,
            justifyContent: legendAlign,
            alignItems: 'center',
            overflow: 'hidden',
            flexWrap: 'nowrap',
            minWidth: 0,
          }}
        >
          {paginatedVehicles.map((vehicle, index) => {
            const color =
              alertColorMapping?.[vehicle] ||
              defaultColors[(page * ITEMS_PER_PAGE + index) % defaultColors.length];
            return (
              <Box
                key={vehicle}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: color,
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '12px', sm: '14px' },
                    lineHeight: '21px',
                    color: '#1B1B1B',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {vehicle}
                </Typography>
              </Box>
            );
          })}
          {/* {fleetAverage && fleetAverage.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 1,
                minWidth: 0,
                maxWidth: '100%',
                ml: 1,
              }}
            >
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#1B1B1B',
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '12px', sm: '14px' },
                  lineHeight: '21px',
                  color: '#1B1B1B',
                }}
              >
                Fleet Avg
              </Typography>
            </Box>
          )} */}
        </Box>

        {showNextButton && (
          <IconButton
            size="small"
            onClick={handleNext}
            sx={{
              p: 0.5,
              color: theme.palette.text.primary,
              flexShrink: 0,
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <ArrowForwardIosIcon sx={{ width: 16, height: 16, opacity: 1 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  const handelTable = () => {
    if (onTableIconClick) {
      onTableIconClick();
    }
    setIsTableOpen(!isTableOpen);
  };

  // Function to get table data for the modal - show all data
  const getTableData = () => {
    if (!data || data.length === 0) return [];

    return data.map((item) => ({
      vehicleId: item.vehicle_id,
      date: item.record_date,
      warningPer100Km: item.warning_ratio.toFixed(2),
    }));
  };

  // Function to get table columns
  const getTableColumns = () => {
    return [
      { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
      { key: 'date', label: 'Date', align: 'left' as const },
      { key: 'warningPer100Km', label: 'Warning per 100 Km', align: 'left' as const },
    ];
  };

  return (
    <>
      <Box
        className="w-full h-full flex flex-col border rounded-lg p-4 items-start mt-2 bg-white"
        sx={{ gap: 1 }}
      >
        <Box className="w-full flex justify-between items-center">
          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Typography
              variant="h6"
              fontSize={18}
              sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
            >
              {title}
            </Typography>
            {infoIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 16,
                  height: 16,
                }}
              >
                {infoIcon}
              </Box>
            )}
          </Box>

          <Box className="flex items-center" sx={{ gap: 1 }}>
            <Box
              className="flex items-center cursor-pointer"
              onClick={handelTable}
              sx={{ pt: '10px', gap: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <img src={eyeIcon} alt="Table" width={24} height={24} style={{ display: 'block' }} />
              </Box>
              <Typography
                variant="body2"
                fontSize={14}
                sx={(theme) => ({ color: theme.palette.primary.main })}
              >
                Table
              </Typography>
            </Box>
            <Box className="flex items-center gap-1 cursor-pointer" sx={{ pt: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  sx={{
                    p: 0,
                    width: '24px !important',
                    height: '24px !important',
                    minWidth: '24px !important',
                    minHeight: '24px !important',
                    maxWidth: '24px !important',
                    maxHeight: '24px !important',
                    flexShrink: 0,
                    display: 'flex !important',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={handleMenuClick}
                >
                  <img
                    src={menuIcon}
                    alt="Menu"
                    width="24"
                    height="24"
                    style={{
                      display: 'block',
                      width: '24px',
                      height: '24px',
                      minWidth: '24px',
                      minHeight: '24px',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {chartData.length > 0 && (
          <Box className="w-full flex flex-col justify-start items-start mb-2">
            {renderCustomizedLegend()}
          </Box>
        )}

        <Box className="w-full h-80 flex">
          {chartData.length > 0 ? (
            <>
              <Typography
                variant="caption"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {yAxisLabel}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                    <CartesianGrid vertical={false} stroke="#e0e0e0" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, dy: 10 }}
                      padding={{ left: 30, right: 30 }}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 14 }}
                      width={40}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />

                    {/* Fleet average highlighted line */}
                    {fleetAverage && fleetAverage.length > 0 && (
                      <Area
                        key="fleetAvg"
                        type="monotone"
                        dataKey="fleetAvg"
                        name="Fleet Avg"
                        stroke="#013220"
                        strokeWidth={1}
                        dot={false}
                        fill="#bcffe7ff"
                        fillOpacity={0.1}
                      />
                    )}

                    {paginatedVehicles.map((vehicle, index) => {
                      const color =
                        alertColorMapping?.[vehicle] ||
                        defaultColors[(page * ITEMS_PER_PAGE + index) % defaultColors.length];

                      return (
                        <Area
                          key={vehicle}
                          type="monotone"
                          dataKey={vehicle}
                          stroke={color}
                          fill={color}
                          strokeWidth={1}
                          fillOpacity={0.1}
                          dot={false}
                        />
                      );
                    })}
                  </AreaChart>
                </ResponsiveContainer>
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                    pt: 1,
                  }}
                >
                  Date
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        open={isTableOpen}
        onClose={() => setIsTableOpen(false)}
        aria-labelledby="warning-per-km-table-modal"
        aria-describedby="warning-per-km-table-description"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            height: '90%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          {tableColumns && tableData ? (
            <CommonAnalysisTable
              title={tableTitle || 'Table View'}
              headerText={tableHeaderText || ''}
              columns={tableColumns}
              data={tableData}
              noDataMessage="No data available"
              onClose={() => setIsTableOpen(false)}
              paginationInfo={paginationInfo}
              onPaginationChange={onTablePaginationChange || onPaginationChange}
            />
          ) : (
            <NumericalAnalysisTable />
          )}
        </Box>
      </Modal>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>

      {/* Expanded Modal */}
      <Modal open={isExpandedModalOpen} onClose={handleCloseExpandedModal}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            height: '80%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: theme.palette.primary.main,
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chart Content - Flex grow to fill space */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {chartData.length > 0 ? (
              <Box sx={{ display: 'flex', flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    textAlign: 'center',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                  }}
                >
                  {yAxisLabel || 'Warning per 100 Km'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 30, bottom: 5 }}>
                      <CartesianGrid vertical={false} stroke="#e0e0e0" />
                      <XAxis
                        dataKey="date"
                        padding={{ left: 40, right: 40 }}
                        axisLine={false}
                        tickLine={false}
                        tick={{ dy: 10 }}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 14 }} width={40} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      {fleetAverage && fleetAverage.length > 0 && (
                        <Area
                          key="fleetAvg_modal"
                          type="monotone"
                          dataKey="fleetAvg"
                          name="Fleet Avg"
                          stroke="#1B1B1B"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          fill="none"
                        />
                      )}

                      {paginatedVehicles.map((vehicle, index) => {
                        const color =
                          alertColorMapping?.[vehicle] ||
                          defaultColors[(page * ITEMS_PER_PAGE + index) % defaultColors.length];
                        return (
                          <Area
                            key={vehicle}
                            type="monotone"
                            dataKey={vehicle}
                            stroke={color}
                            fill={color}
                            fillOpacity={0.1}
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontWeight: 400,
                      fontSize: 12,
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      color: '#1B1B1B',
                      pt: 1,
                    }}
                  >
                    Date
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Edit Data Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        cardTitle={title}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />
    </>
  );
};

export default CommonFloatingGraph;
